/**
 * attachmentUploadService
 *
 * Domain-agnostic upload for event attachments (images + documents) on ANY
 * domain event — mentions, recognitions, attendance, academics, health, etc.
 *
 * Reuses the MediaUploadService pipeline (HEIC→JPEG, compress, thumbnail,
 * resumable progress, 10MB cap) via uploadToPath, then maps the result into
 * the unified AttachmentRecord shape that gets embedded on the event document.
 *
 * Storage path: schools/{schoolId}/attachments/{domain}/{sessionId}/{attachmentId}/{fileName}
 * The path is opaque — the event↔attachment link is the embedded array on the
 * event doc, not the path. sessionId is used because the eventId is not known
 * until the event is created.
 *
 * @module services/attachments/attachmentUploadService
 */

import MediaUploadService from '../messaging/MediaUploadService';
import { deleteObject, ref } from 'firebase/storage';
import { storage } from '@/firebase';

/**
 * @typedef {Object} AttachmentRecord
 * @property {string} id
 * @property {'image'|'document'} kind
 * @property {string} storagePath
 * @property {string} url
 * @property {string} fileName
 * @property {string} mimeType
 * @property {number} fileSize
 * @property {number|null} width
 * @property {number|null} height
 * @property {string|null} thumbnailUrl
 * @property {string|null} thumbnailStoragePath
 * @property {string} uploadedBy
 * @property {string} uploadedByName
 * @property {number} uploadedAt
 */

/** Generate a client-side attachment id (matches MediaUploadService id scheme). */
export function newAttachmentId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

/** Generate a session id for grouping attachments uploaded before an event exists. */
export function newSessionId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

/**
 * Upload one attachment and return its embeddable record.
 *
 * @param {File} file
 * @param {Object} ctx
 * @param {string} ctx.schoolId
 * @param {string} ctx.domain - domain id (mentions, recognitions, academics, attendance, ...)
 * @param {string} ctx.sessionId - groups files for one in-progress event
 * @param {string} ctx.uploadedBy - uid
 * @param {string} ctx.uploadedByName
 * @param {Function} [ctx.onProgress] - progress callback (0-100)
 * @returns {Promise<AttachmentRecord>}
 */
export async function uploadAttachment(file, { schoolId, domain, sessionId, uploadedBy, uploadedByName, onProgress }) {
  if (!schoolId) throw new Error('uploadAttachment: schoolId is required');
  if (!domain) throw new Error('uploadAttachment: domain is required');

  const id = newAttachmentId();
  const basePath = `schools/${schoolId}/attachments/${domain}/${sessionId || 'draft'}/${id}`;

  const result = await MediaUploadService.uploadToPath(basePath, file, onProgress);

  return {
    id,
    kind: result.type === 'image' ? 'image' : 'document',
    storagePath: result.storagePath,
    url: result.url,
    fileName: result.fileName,
    mimeType: result.mimeType,
    fileSize: result.fileSize,
    width: result.width,
    height: result.height,
    thumbnailUrl: result.thumbnailUrl,
    thumbnailStoragePath: result.thumbnailStoragePath || null,
    uploadedBy: uploadedBy || null,
    uploadedByName: uploadedByName || null,
    uploadedAt: Date.now(),
  };
}

export async function removeAttachment(attachment) {
  if (!attachment?.storagePath) return;
  await deleteObject(ref(storage, attachment.storagePath));

  if (attachment.thumbnailStoragePath) {
    await deleteObject(ref(storage, attachment.thumbnailStoragePath));
  }
}

export async function removeAttachments(attachments = []) {
  const results = await Promise.allSettled(attachments.map(removeAttachment));
  const failure = results.find(result => result.status === 'rejected');
  if (failure) throw failure.reason;
}

export default {
  uploadAttachment,
  removeAttachment,
  removeAttachments,
  newAttachmentId,
  newSessionId,
};
